/**
 * ENG-6245: guard against oversized / data-URI agent avatars bricking the
 * slack-channel MCP.
 *
 * The manager threads `agents.avatar_url` into the slack-channel MCP as the
 * `SLACK_AGENT_AVATAR_URL` env var (ENG-6155), which the bot applies as its
 * Slack profile photo. `posix_spawn` caps a single argv/env entry at
 * `MAX_ARG_STRLEN` (128 KiB on Linux). A base64 `data:` URI avatar (~1.5 MB
 * seen in prod on maven 2026-06-10 / sherlock 2026-06-09) blows past that → the
 * spawn fails **E2BIG** → the slack MCP never starts → the presence reaper
 * quarantines Slack (ENG-5932) while every other channel stays healthy. Because
 * the avatar isn't part of the channel-config hash, clearing the quarantine
 * just re-provisions the same oversized env and E2BIGs again.
 *
 * The fix is to never inject an avatar value that can't be a hosted URL: a
 * `data:` URI (which should have been uploaded to storage, not inlined) or any
 * value past a conservative byte cap well under MAX_ARG_STRLEN. Skipping it
 * degrades gracefully — the bot simply keeps its current photo.
 *
 * This module is intentionally pure and dependency-free (no `node:*`, no
 * `Buffer`) so it stays browser/edge-bundleable alongside the rest of the
 * provisioning barrel, and is unit-testable without the full adapter.
 */

/**
 * Byte cap for an avatar URL injected as an env var value. A real hosted avatar
 * URL (Supabase Storage public URL + `?v=` cache-bust) is a few hundred bytes;
 * 8 KiB leaves enormous headroom for legitimate URLs while staying ~16× under
 * the 128 KiB `MAX_ARG_STRLEN` `posix_spawn` limit.
 */
export const MAX_AVATAR_ENV_URL_BYTES = 8192;

export type AvatarEnvSkipReason = 'empty' | 'data-uri' | 'too-large';

export interface AvatarEnvResolution {
  /** The URL safe to inject, or `null` when it must be skipped. */
  url: string | null;
  /** Why the URL was skipped (absent when `url` is non-null). */
  skipReason?: AvatarEnvSkipReason;
  /** UTF-8 byte length of the trimmed input (populated when skipped for size or data-URI). */
  bytes?: number;
}

/** UTF-8 byte length without depending on `Buffer` (keeps this edge-safe). */
function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Decide whether an agent avatar URL is safe to inject as an env var value
 * (e.g. `SLACK_AGENT_AVATAR_URL`). Returns the trimmed URL when safe, or a
 * `null` URL plus a skip reason when it is empty, a `data:` URI, or oversized.
 *
 * Pure + reusable so both the framework adapter (the structural guard that
 * keeps the env entry from ever being written) and the manager (the
 * operator-visible warning log) share one definition of "safe".
 */
export function resolveAvatarEnvUrl(raw: string | null | undefined): AvatarEnvResolution {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (trimmed === '') {
    return { url: null, skipReason: 'empty' };
  }
  // A `data:` URI means avatar generation inlined the image instead of
  // uploading it to storage (the pre-ENG-5717 fallback). Reject by scheme, not
  // just by size — even a small data URI is the wrong shape here (not a hosted
  // URL) and it's the exact thing that blew the arg limit in prod.
  if (/^data:/i.test(trimmed)) {
    return { url: null, skipReason: 'data-uri', bytes: utf8ByteLength(trimmed) };
  }
  const bytes = utf8ByteLength(trimmed);
  if (bytes > MAX_AVATAR_ENV_URL_BYTES) {
    return { url: null, skipReason: 'too-large', bytes };
  }
  return { url: trimmed };
}
